import { Link } from "react-router-dom";
import {
  HouseLine, Trophy, BookOpenText, CalendarBlank, Wrench, Umbrella, ArrowSquareOut, SignOut,
  ArrowCounterClockwise, Calculator, Lock, Gear, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { NEW_FEATURES, getSeenFeatures, markFeatureSeen } from "../version";
import { useSheetReconnect } from "../hooks/useSheetReconnect";

const COLOR = {
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
};

const NAV_ITEMS = [
  { to: "/app", label: "Daily Entry", icon: HouseLine, id: "daily" },
  { to: "/app/calculator", label: "Relay Calculator", icon: Calculator, id: "calculator" },
  { to: "/app/history", label: "Entry History", icon: ClockCounterClockwise, id: "history" },
  { to: "/app/timeoff", label: "Time Off & PTO", icon: Umbrella, id: "timeoff" },
  { to: "/app/monthly", label: "Monthly Wrap-Up", icon: CalendarBlank, id: "monthly", seasonOnly: true },
  { to: "/app/scoreboard", label: "Yearly Scoreboard", icon: Trophy, id: "scoreboard" },
  { to: "/app/journal", label: "Reflection Journal", icon: BookOpenText, id: "journal" },
  { to: "/app/resources", label: "Resource Library", icon: Wrench, id: "resources", seasonOnly: true },
  { to: "/app/settings", label: "Settings", icon: Gear, id: "settings" },
];

export const CaddySidebar = ({ active, sheetUrl, onSignOut, onFreshStart, betaChip, sprint = false }) => {
  const { needsReconnect } = useSheetReconnect();
  return (
  <aside
    className="hidden lg:flex flex-col lg:sticky lg:top-8 lg:self-start lg:pr-7"
    style={{ background: "transparent", borderRight: "0.5px solid var(--pr-sidebar-divider)" }}
    data-testid="caddy-sidebar"
  >
    <div className="flex items-center gap-2 mb-6">
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--pr-muted)" }}>
        Payday Relay
      </span>
      {betaChip}
    </div>
    <nav className="space-y-1">
      {NAV_ITEMS.map(({ to, label, icon: Icon, id, seasonOnly }) => {
        const isActive = id === active;
        const locked = sprint && seasonOnly;
        const isNew = !locked && NEW_FEATURES.includes(id) && !getSeenFeatures().has(id);
        return (
          <Link
            key={id}
            to={to}
            onClick={() => { if (isNew) markFeatureSeen(id); }}
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors"
            data-active={isActive ? "true" : "false"}
            style={isActive
              ? { background: "var(--pr-ink-surface)", color: "#fff", fontWeight: 600, boxShadow: "0 1px 2px rgba(42,30,92,0.08)" }
              : { color: COLOR.ink2, fontWeight: 500 }}
            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(0,0,0,0.05)"; }}
            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            data-testid={`sidebar-nav-${id}`}
            title={locked ? "Season Pass feature, unlock with your 52-week race" : undefined}
          >
            <Icon size={17} weight={isActive ? "fill" : "regular"} className="flex-shrink-0 translate-y-[0.5px]" style={locked ? { opacity: 0.5 } : undefined} />
            <span style={locked ? { opacity: 0.55 } : undefined}>{label}</span>
            {locked && <Lock size={12} weight="fill" className="ml-auto flex-shrink-0" style={{ color: "var(--pr-muted)" }} data-testid={`sidebar-lock-${id}`} />}
            {isNew && (
              <span
                className="ml-auto text-[9px] font-bold tracking-[0.12em]"
                style={{ color: isActive ? "#fff" : "var(--pr-ink)" }}
                data-testid={`new-badge-${id}`}
              >
                NEW
              </span>
            )}
            {id === "settings" && needsReconnect && (
              <span
                className="ml-auto flex-shrink-0 inline-flex items-center justify-center"
                data-testid="sidebar-alert-settings"
                title="Action needed: reconnect Google"
                style={{ width: 16, height: 16, borderRadius: "50%", background: "#DE3C4B", color: "#fff", fontSize: 11, fontWeight: 800, lineHeight: 1 }}
              >
                !
              </span>
            )}
          </Link>
        );
      })}
    </nav>
    <div className="my-4 border-t" style={{ borderColor: COLOR.line }} />
    {sheetUrl && (
      <a
        href={sheetUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors"
        style={{ color: COLOR.ink2 }}
        data-testid="sidebar-open-sheet"
      >
        <ArrowSquareOut size={17} weight="regular" className="flex-shrink-0 translate-y-[0.5px]" />
        Open my sheet
      </a>
    )}
    {onFreshStart && (
      <button
        onClick={onFreshStart}
        className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium text-left transition-colors"
        style={{ color: COLOR.muted }}
        data-testid="sidebar-fresh-start"
      >
        <ArrowCounterClockwise size={17} weight="regular" className="flex-shrink-0 translate-y-[0.5px]" />
        Start a Fresh Relay
      </button>
    )}
    {onSignOut && (
      <button
        onClick={onSignOut}
        className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium text-left transition-colors"
        style={{ color: COLOR.muted }}
        data-testid="sidebar-signout"
      >
        <SignOut size={17} weight="regular" className="flex-shrink-0 translate-y-[0.5px]" />
        Sign out
      </button>
    )}
  </aside>
  );
};
